// src/context/AuthContext.tsx - Updated for separated admin_users and game_hosts tables
import React, {createContext, useContext, useEffect, useState} from 'react';
import {supabase} from '@/services/supabase';
import type {User} from '@/types';

interface AuthState {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
    signIn: (email: string, password: string) => Promise<void>;
    signOut: () => Promise<void>;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Get user data from either admin_users or game_hosts table
const getUserData = async (supabaseUserId: string, email: string, session?: any): Promise<User | null> => {
    try {
        // Check if this is an invitation flow with IDs in JWT metadata
        const adminIdFromJWT = session?.user?.user_metadata?.admin_id;
        const hostIdFromJWT = session?.user?.user_metadata?.host_id;

        console.log('🔍 getUserData - Auth ID:', supabaseUserId, 'JWT admin_id:', adminIdFromJWT, 'JWT host_id:', hostIdFromJWT);

        // Try admin_users table - check both JWT admin_id AND auth user ID
        let adminData = null;

        // First try JWT admin_id if it exists
        if (adminIdFromJWT) {
            const {data, error} = await supabase
                .from('admin_users')
                .select('*')
                .eq('id', adminIdFromJWT)
                .maybeSingle();

            if (data && !error) {
                adminData = data;
                console.log('✅ Found admin using JWT admin_id');
            }
        }

        // If JWT admin_id didn't work, try auth user ID
        if (!adminData) {
            const {data, error} = await supabase
                .from('admin_users')
                .select('*')
                .eq('id', supabaseUserId)
                .maybeSingle();

            if (data && !error) {
                adminData = data;
                console.log('✅ Found admin using auth user ID');
            }
        }

        // If JWT admin_id and auth user ID didn't work, try email lookup (fallback)
        if (!adminData) {
            const {data, error} = await supabase
                .from('admin_users')
                .select('*')
                .eq('email', email)
                .maybeSingle();

            if (data && !error) {
                adminData = data;
                console.log('✅ Found admin using email lookup');
            }
        }

        // If we found admin data, return it
        if (adminData) {
            return {
                id: adminData.id,
                email: adminData.email,
                first_name: adminData.first_name || null,
                last_name: adminData.last_name || null,
                full_name: adminData.full_name || adminData.email,
                role: adminData.role,
                organization_id: adminData.organization_id || null,
                account_id: adminData.account_id || null,
                status: adminData.status || 'active',
                created_at: adminData.created_at,
                updated_at: adminData.updated_at,
            };
        }

        // Try game_hosts table - check both JWT host_id AND email lookup
        let hostData = null;

        // First try JWT host_id if it exists
        if (hostIdFromJWT) {
            const {data, error} = await supabase
                .from('game_hosts')
                .select('*')
                .eq('id', hostIdFromJWT)
                .maybeSingle();

            if (data && !error) {
                hostData = data;
                console.log('✅ Found host using JWT host_id');
            }
        }

        // If JWT host_id didn't work, try email lookup
        if (!hostData) {
            const {data, error} = await supabase
                .from('game_hosts')
                .select('*')
                .eq('email', email)
                .eq('invitation_status', 'accepted')
                .maybeSingle();

            if (data && !error) {
                hostData = data;
                console.log('✅ Found host using email lookup');
            }
        }

        // If we found host data, return it
        if (hostData) {
            return {
                id: hostData.id,
                email: hostData.email,
                first_name: hostData.first_name || null,
                last_name: hostData.last_name || null,
                full_name: hostData.full_name || hostData.email,
                role: 'host',
                organization_id: null,
                account_id: hostData.account_id || null,
                status: hostData.status || 'active',
                created_at: hostData.created_at,
                updated_at: hostData.updated_at,
            };
        }

        console.error('User not found in admin_users or game_hosts tables');
        return null;
    } catch (error) {
        console.error('Error fetching user data:', error);
        return null;
    }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const checkUser = async () => {
        try {
            const {data: {session}} = await supabase.auth.getSession();

            if (session?.user) {
                // Try to get user data from database
                const userData: User | null = await getUserData(session.user.id, session.user.email || '', session);

                if (userData) {
                    // Normal case: user exists in database
                    setUser(userData);
                    setIsAuthenticated(true);
                } else {
                    // Handle invitation case or sign out invalid users
                    console.log('⚠️ No user record found, checking if this is an invitation flow');

                    const sessionUser = session.user;
                    if (sessionUser.user_metadata?.role === 'host') {
                        // This is a game host in the invitation process
                        const minimalUser: User = {
                            id: sessionUser.id,
                            email: sessionUser.email || '',
                            first_name: sessionUser.user_metadata?.first_name || null,
                            last_name: sessionUser.user_metadata?.last_name || null,
                            full_name: sessionUser.user_metadata?.full_name || sessionUser.email || 'User',
                            role: 'host',
                            organization_id: null,
                            account_id: sessionUser.user_metadata?.account_id || null,
                            status: 'active',
                            created_at: sessionUser.created_at || new Date().toISOString(),
                            updated_at: sessionUser.updated_at || new Date().toISOString(),
                        };
                        setUser(minimalUser);
                        setIsAuthenticated(true);
                    } else {
                        // Unknown user type, sign them out
                        console.error('Unknown user type, signing out');
                        await supabase.auth.signOut();
                        setUser(null);
                        setIsAuthenticated(false);
                    }
                }
            } else {
                setUser(null);
                setIsAuthenticated(false);
            }
        } catch (error) {
            console.error('Auth check error:', error);
            setUser(null);
            setIsAuthenticated(false);
        }

        setIsLoading(false);
    };

    useEffect(() => {
        // Initial check
        checkUser();

        // Listen for auth changes
        const {data: {subscription}} = supabase.auth.onAuthStateChange((event, session) => {
            console.log('Auth state changed:', event, session?.user?.email);

            if (event === 'SIGNED_OUT') {
                setUser(null);
                setIsAuthenticated(false);
                setIsLoading(false);
            } else {
                // For all other events, re-check the user
                checkUser();
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const signIn = async (email: string, password: string): Promise<void> => {
        try {
            const {data, error} = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) throw error;

            if (data.user) {
                const userData = await getUserData(data.user.id, data.user.email || '');
                if (userData) {
                    setUser(userData);
                    setIsAuthenticated(true);
                } else {
                    throw new Error('User profile not found');
                }
            }
        } catch (error: any) {
            throw new Error(error.message || 'Failed to sign in');
        }
    };

    const signOut = async (): Promise<void> => {
        try {
            // Clear state immediately
            setUser(null);
            setIsAuthenticated(false);

            // Sign out from Supabase
            await supabase.auth.signOut();

            // Force redirect
            window.location.href = '/login';
        } catch (error) {
            console.error('Sign out error:', error);
            // Force redirect even on error
            window.location.href = '/login';
        }
    };

    const refreshUser = async (): Promise<void> => {
        if (user) {
            await checkUser();
        }
    };

    const value: AuthContextType = {
        user,
        isLoading,
        isAuthenticated,
        signIn,
        signOut,
        refreshUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
